import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Select, EmptyState, TimelineFrame, TimelineControls, TimelineGroupControl, TimelineDateBand, TimelineGroupRail, TimelineEvent, TimelineUndated } from '../ui/index.js';
import { STAGE_LABELS, COLLECTION_STAGE_IDS } from '../../shared/stages.mjs';
import { stagePresentation } from '../presentation/stage-presentation.mjs';
import { moneyText, formatDateOnly } from '../lib/formatters.mjs';
import { IconChevronLeft, IconChevronRight, IconChevronDown, IconMinus, IconPlus, IconCalendar } from '../ui/icons.js';
import {
  DAY_MS, clampAnchor, clampDayWidth, timelineWindow, rowDates, initialAnchor, zoomCenterAnchor,
  stepAnchor, barGeometry, readTimelinePreference, writeTimelinePreference, calendarBands,
} from './timeline-math.mjs';
import './campaign-timeline.css';

const LABELS = STAGE_LABELS;
const ORDER = COLLECTION_STAGE_IDS;
const GROUPS = [{ value: 'stage', label: 'Aşama' }, { value: 'location', label: 'Şehir' }, { value: 'none', label: 'Gruplama yok' }];
const MONTH = new Intl.DateTimeFormat('tr-TR', { month: 'long', timeZone: 'UTC' });
const SHORT_DATE = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const scaleLabel = scale => ({ week: 'Hafta', month: 'Ay', year: 'Yıl' })[scale];
const stageKey = row => row.stage || 'RECOMMENDED';
function storage() { try { return globalThis.localStorage; } catch { return null; } }
function groupRows(rows, mode) {
  if (mode === 'none') return [{ id: 'all', label: 'Tüm fırsatlar', rows }];
  if (mode === 'location') {
    const places = [...new Set(rows.map(row => row.location || 'Konum netleşiyor'))].sort((a, b) => a.localeCompare(b, 'tr'));
    return places.map(place => ({ id: 'location:' + place, label: place, rows: rows.filter(row => (row.location || 'Konum netleşiyor') === place) }));
  }
  const stages = [...new Set(rows.map(stageKey))].sort((a, b) => {
    const ai = ORDER.indexOf(a), bi = ORDER.indexOf(b);
    return (ai < 0 ? ORDER.length : ai) - (bi < 0 ? ORDER.length : bi);
  });
  return stages.map(stage => ({ id: 'stage:' + stage, label: rows.find(row => stageKey(row) === stage)?.stageLabel || LABELS[stage] || stage, rows: rows.filter(row => stageKey(row) === stage) }));
}
function GroupSelect({ value, onChange }) {
  return <TimelineGroupControl><Select value={value} onValueChange={onChange} options={GROUPS} size="sm" aria-label="Timeline grupla" /></TimelineGroupControl>;
}
export function CampaignTimeline(props) {
  return <TimelineContent key={props.preferenceKey || props.campaign?.id || 'campaign-timeline'} {...props} />;
}
function TimelineContent({ rows = [], onOpen, preferenceKey = 'bisponsor-timeline', campaign }) {
  const seed = useRef(null), restored = useRef(false);
  if (!seed.current) {
    const saved = readTimelinePreference(storage(), preferenceKey); restored.current = Boolean(saved);
    seed.current = saved || {
    version: 1, anchor: initialAnchor(campaign, rows), dayWidth: 24, scale: 'week', group: 'stage', collapsed: [], top: 0,
    };
  }
  const [view, setView] = useState(() => ({ year: timelineWindow(seed.current.anchor).year, width: seed.current.dayWidth, scale: seed.current.scale, group: seed.current.group, collapsed: seed.current.collapsed }));
  const attachedElement = useRef(null);
  const scrollRef = useRef(null), pending = useRef(seed.current), last = useRef(seed.current), ready = useRef(false), saving = useRef(null), gesture = useRef(null);
  const window = useMemo(() => timelineWindow(seed.current.anchor, view.year), [view.year]);
  const geometry = useRef(null), navigateRef = useRef(null);
  geometry.current = { ...view, ...window };
  const bands = useMemo(() => calendarBands(window, view.scale), [window, view.scale]);
  const datedRows = useMemo(() => rows.filter(row => rowDates(row)), [rows]);
  const undatedRows = useMemo(() => rows.filter(row => !rowDates(row)), [rows]);
  const groups = useMemo(() => groupRows(datedRows, view.group), [datedRows, view.group]);
  const hasDatedRows = datedRows.length > 0;
  const railWidth = el => parseFloat(getComputedStyle(el).getPropertyValue('--timeline-rail')) || 220;
  const centerOffset = el => Math.max(0, (el.clientWidth - railWidth(el)) / 2);
  function snapshot() {
    const el = scrollRef.current, current = geometry.current;
    if (!el || !ready.current) return last.current;
    return { version: 1, anchor: clampAnchor(current.start + (el.scrollLeft + centerOffset(el)) / current.width * DAY_MS),
      dayWidth: current.width, scale: current.scale, group: current.group, collapsed: current.collapsed, top: el.scrollTop };
  }
  function persist(value, immediate = false) {
    if (!value) return;
    last.current = value;
    clearTimeout(saving.current);
    if (immediate) writeTimelinePreference(storage(), preferenceKey, value);
    else saving.current = setTimeout(() => writeTimelinePreference(storage(), preferenceKey, last.current), 120);
  }
  function navigate(anchor, width = geometry.current.width, scale = geometry.current.scale, top = scrollRef.current?.scrollTop || 0) {
    anchor = clampAnchor(anchor); width = clampDayWidth(width);
    const current = geometry.current;
    let year = current.year;
    if (anchor < current.start || anchor >= current.end) year = timelineWindow(anchor).year;
    pending.current = { version: 1, anchor, dayWidth: width, scale, top, group: current.group, collapsed: current.collapsed };
    setView(value => ({ ...value, year, width, scale }));
  }
  navigateRef.current = navigate;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const value = pending.current || (attachedElement.current !== el ? last.current : null);
    if (!value) return;
    if (!ready.current && !restored.current) value.anchor = initialAnchor(campaign, rows);
    if (value.anchor < window.start || value.anchor >= window.end) {
      pending.current = value;
      setView(previous => ({ ...previous, year: timelineWindow(value.anchor).year }));
      return;
    }
    attachedElement.current = el;
    el.scrollLeft = Math.max(0, (value.anchor - window.start) / DAY_MS * view.width - centerOffset(el));
    el.scrollTop = value.top || 0;
    pending.current = null; ready.current = true;
    persist({ ...value, group: view.group, collapsed: view.collapsed }, true);
  }, [view, window.start, rows.length]);
  useEffect(() => () => {
    clearTimeout(saving.current);
    writeTimelinePreference(storage(), preferenceKey, last.current);
  }, [preferenceKey]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const wheel = event => {
      if ((!event.ctrlKey && !event.metaKey) || !event.deltaY) return;
      const rect = el.getBoundingClientRect(), rail = railWidth(el);
      if (event.clientX < rect.left + rail) return;
      event.preventDefault();
      const current = geometry.current, next = clampDayWidth(current.width * Math.exp(-event.deltaY * .002));
      const point = event.clientX - rect.left - rail - centerOffset(el);
      navigateRef.current(zoomCenterAnchor({ anchor: snapshot().anchor, fromWidth: current.width, toWidth: next, cursorOffset: point }), next, current.scale);
    };
    const touchstart = event => {
      if (event.touches.length !== 2) return;
      const [a, b] = event.touches, distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const rect = el.getBoundingClientRect(), x = (a.clientX + b.clientX) / 2, rail = railWidth(el);
      if (distance < 1 || x < rect.left + rail) return;
      const current = geometry.current;
      gesture.current = { distance, width: current.width, anchorAtPoint: current.start + (el.scrollLeft + x - rect.left - rail) / current.width * DAY_MS };
    };
    const touchmove = event => {
      const initial = gesture.current;
      if (!initial || event.touches.length !== 2) return;
      event.preventDefault();
      const [a, b] = event.touches, next = clampDayWidth(initial.width * Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) / initial.distance);
      const x = (a.clientX + b.clientX) / 2 - el.getBoundingClientRect().left - railWidth(el);
      navigateRef.current(initial.anchorAtPoint + (centerOffset(el) - x) / next * DAY_MS, next, geometry.current.scale);
    };
    const touchend = () => { gesture.current = null; };
    el.addEventListener('wheel', wheel, { passive: false }); el.addEventListener('touchstart', touchstart, { passive: false }); el.addEventListener('touchmove', touchmove, { passive: false });
    el.addEventListener('touchend', touchend); el.addEventListener('touchcancel', touchend);
    let previous = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === previous || !ready.current) return;
      previous = el.clientWidth;
      if (last.current) navigateRef.current(last.current.anchor, geometry.current.width, geometry.current.scale, last.current.top);
    });
    observer.observe(el);
    return () => {
      observer.disconnect(); el.removeEventListener('wheel', wheel); el.removeEventListener('touchstart', touchstart); el.removeEventListener('touchmove', touchmove);
      el.removeEventListener('touchend', touchend); el.removeEventListener('touchcancel', touchend);
    };
  }, [hasDatedRows]);
  function onScroll() {
    if (!ready.current || pending.current) return;
    const el = scrollRef.current, current = geometry.current;
    const value = snapshot(); persist(value);
    const max = el.scrollWidth - el.clientWidth;
    if ((el.scrollLeft < 100 || el.scrollLeft > max - 100) && max > 0) {
      const nextYear = timelineWindow(value.anchor, current.year + (el.scrollLeft < 100 ? -2 : 2)).year;
      if (nextYear !== current.year) {
        pending.current = value;
        setView(previous => ({ ...previous, year: nextYear }));
      }
    }
  }
  function changeGroup(group) {
    const value = snapshot();
    pending.current = { ...value, group, collapsed: [] };
    setView(previous => ({ ...previous, group, collapsed: [] }));
  }
  function toggleGroup(id) {
    const collapsed = view.collapsed.includes(id) ? view.collapsed.filter(value => value !== id) : [...view.collapsed, id];
    pending.current = { ...snapshot(), collapsed };
    setView(previous => ({ ...previous, collapsed }));
  }
  function changeScale() {
    const next = view.scale === 'week' ? 'month' : view.scale === 'month' ? 'year' : 'week';
    navigate(snapshot().anchor, next === 'week' ? 24 : next === 'month' ? 8 : 4, next);
  }
  const now = new Date(), today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()), todayIndex = (today - window.start) / DAY_MS;
  return <TimelineFrame aria-label="Kampanya zaman çizelgesi" data-timeline-scale={view.scale} data-day-width={view.width}>
    <TimelineControls>
      <div className="timeline-navigation">
        <Button size="sm" variant="ghost" data-action="timeline-today" onClick={() => navigate(today)}>Bugün</Button>
        <Button size="sm" variant="ghost" aria-label="Önceki dönem" data-action="timeline-prev" onClick={() => navigate(stepAnchor(snapshot().anchor, view.scale, -1))}><IconChevronLeft size={16} /></Button>
        <Button size="sm" variant="ghost" aria-label="Zaman ölçeği" data-action="timeline-scale" title="Hafta, ay ve yıl ölçekleri arasında geçiş yapın" onClick={changeScale}>{scaleLabel(view.scale)}</Button>
        <Button size="sm" variant="ghost" aria-label="Sonraki dönem" data-action="timeline-next" onClick={() => navigate(stepAnchor(snapshot().anchor, view.scale, 1))}><IconChevronRight size={16} /></Button>
        <Button size="sm" variant="ghost" aria-label="Uzaklaştır" data-action="timeline-zoom-out" disabled={view.width <= 4} onClick={() => navigate(snapshot().anchor, view.width - 4)}><IconMinus size={16} /></Button>
        <Button size="sm" variant="ghost" aria-label="Yakınlaştır" data-action="timeline-zoom-in" disabled={view.width >= 64} onClick={() => navigate(snapshot().anchor, view.width + 4)}><IconPlus size={16} /></Button>
      </div>
      <GroupSelect value={view.group} onChange={changeGroup} />
    </TimelineControls>
    {datedRows.length ? <div ref={scrollRef} className="timeline-scroll" role="region" tabIndex={0} aria-label="Yatay zaman çizelgesi; tarihleri görmek için kaydırın" onScroll={onScroll}>
      <div className="timeline-grid" style={{ '--timeline-day': view.width + 'px', '--timeline-width': window.days * view.width + 'px' }}>
        <div className="timeline-header"><div className="timeline-corner" /><div className="timeline-dates">
          <TimelineDateBand items={bands.upper.map(band => ({key:band.key,left:band.start * view.width,width:band.days * view.width,label:view.scale === 'year' ? new Date(band.from).getUTCFullYear() : MONTH.format(band.from) + ' ' + new Date(band.from).getUTCFullYear()}))} />
          <TimelineDateBand lower items={bands.lower.map(band => ({key:band.key,left:band.start * view.width,width:band.days * view.width,label:view.scale === 'year' ? MONTH.format(band.from) : view.scale === 'week' ? SHORT_DATE.format(band.from) + ' – ' + SHORT_DATE.format(band.to) : SHORT_DATE.format(band.from)}))} />
        </div></div>
        <div className="timeline-body">{groups.map(group => <div key={group.id} className={'timeline-lane' + (view.collapsed.includes(group.id) ? ' collapsed' : '')}>
          <TimelineGroupRail label={group.label} count={group.rows.length} collapsed={view.collapsed.includes(group.id)} onToggle={() => toggleGroup(group.id)} />
          <div className="timeline-track" hidden={view.collapsed.includes(group.id)}>{group.rows.map(row => {
            const bar = barGeometry(row, window.start, window.days);
            if (!bar) return null;
            const dates = rowDates(row), label = formatDateOnly(dates.start, { month: 'long' }) + (dates.end !== dates.start ? ' – ' + formatDateOnly(dates.end, { month: 'long' }) : '');
            return <div className="timeline-event-row" key={row.id} data-timeline-row-id={row.id}><TimelineEvent name={row.locked?'Adı kilitli etkinlik':row.name} price={moneyText(row)} review={row.needsReview} tone={stagePresentation(stageKey(row)).tone} stage={stageKey(row)} recordId={row.id} opportunityId={row.opportunityId} title={(row.locked?'Adı kilitli etkinlik':row.name) + ' · ' + label} left={bar.left * view.width} width={bar.days * view.width + 'px'} onOpen={() => onOpen?.(row)} /></div>;
          })}</div>
        </div>)}
        {todayIndex >= 0 && todayIndex < window.days && <div className="timeline-today-line" aria-label="Bugünün tarihi" style={{ left: 'calc(var(--timeline-rail) + ' + todayIndex * view.width + 'px)' }} />}
        </div>
      </div>
    </div> : <EmptyState compact variant={rows.length ? "waiting" : "search"} className="timeline-empty" icon={IconCalendar} title={rows.length ? 'Fırsat tarihleri henüz netleşmedi' : 'Bu filtrelerde fırsat bulunamadı'}>{rows.length ? 'Tarihi netleşmeyen fırsatları aşağıdaki bölümden inceleyebilirsiniz.' : 'Arama veya filtreleri değiştirerek tekrar deneyin.'}</EmptyState>}
    {undatedRows.length > 0 && <TimelineUndated items={undatedRows.map(row => ({id:row.id,name:row.locked?'Adı kilitli etkinlik':row.name,onOpen:() => onOpen?.(row)}))} />}
  </TimelineFrame>;
}
